import { FC, FormEvent, useRef, useState } from "react";
import {
	Alert,
	AlertTitle,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	InputLabel,
	Link,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { BodyText, HeadingText1, HeadingText2 } from "../styles/styledComponents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ScreenBase from "./ScreenBase";
// import useSES from "../hooks/useSES";
import emailjs from "@emailjs/browser";

export type ContactInfo = {
	firstName: string;
	lastName: string;
	email: string;
	signUp: boolean;
	phone: string;
	freeMusic: boolean;
	likeYourStuff: boolean;
	networking: boolean;
	fan: boolean;
	musicStyles: string;
	referralSource: string;
	message: string;
}

const ContactScreen: FC = () => {
	// const ses = useSES();
	const contactForm = useRef<HTMLFormElement>(null);
	
	const [contactInfo, setContactInfo] = useState<ContactInfo>({
		firstName: '',
		lastName: '',
		email: '',
		signUp: false,
		phone: '',
		freeMusic: false,
		likeYourStuff: false,
		networking: false,
		fan: false,
		musicStyles: '',
		referralSource: '',
		message: ''
	});
	const [emailError, setEmailError] = useState('');
	const [submitSuccessful, setSubmitSuccessful] = useState(false);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		setContactInfo({
			...contactInfo,
			[name]: value
		});
	};

	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = event.target;

		setContactInfo({
			...contactInfo,
			[name]: checked
		});
	};

	const handleSelectChange = (event: SelectChangeEvent) => {
		const { name, value } = event.target;

		setContactInfo(prevState => ({
			...prevState,
			[name]: value
		}));
	};

	const validateEmail = () => {
		const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

		if (!contactInfo.email || !contactInfo.email.trim()) {
			setEmailError('Email is required.');
		} else if (!emailPattern.test(contactInfo.email)) {
			setEmailError('Please enter a valid email address.');
		} else {
			setEmailError('');
		}
	};

	const handleSubmitContactForm = (e: FormEvent) => {
		e.preventDefault();

		if (contactForm) {
			emailjs.sendForm('service_4wrt2nq', 'template_c0tkql6', contactForm.current as string | HTMLFormElement, {
				publicKey: '7T3A32RhBxMFjDCRq'
			})
			.then((result) => {
				console.info('Contact form submission result:', result);
				setSubmitSuccessful(true);

				// Clear the form after successful submit.
				setContactInfo({
					...contactInfo,
					firstName: '',
					lastName: '',
					email: '',
					signUp: false,
					phone: '',
					freeMusic: false,
					likeYourStuff: false,
					networking: false,
					fan: false,
					musicStyles: '',
					referralSource: '',
					message: ''
				});
			}, (error) => {
				console.error('Error sending contact form submission:', error);
				setSubmitSuccessful(false);
			});
		}
	};

	return (
		<ScreenBase
			scrollableChildren={(
				<Stack spacing={4}>
					<Stack component="section" spacing={4}>
						<HeadingText1>Contact Tim Kulig</HeadingText1>
						<Alert severity="info" variant="outlined">
							<AlertTitle>PROFESSIONAL SERVICES</AlertTitle>
							I'm available for custom music beds, film scoring, corporate & internet
							video productions, and any other project needing a sonic narrative.
							Contact me below to start collaborating!
						</Alert>
						<Grid container spacing={4}>
							<Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
								<Paper elevation={2} sx={{ px: 4, py: 3 }}>
									<Stack spacing={2}>
										<HeadingText2>Press & Inquiries</HeadingText2>
										<BodyText>
											If you have any questions, site or content suggestions, or comments,
											please contact me via email or phone.
										</BodyText>
									</Stack>
								</Paper>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
								<Paper elevation={2} sx={{ px: 4, py: 3 }}>
									<Stack spacing={2}>
										<HeadingText2>Contact Info</HeadingText2>
										<BodyText>
											<Stack spacing={0}>
												<Stack direction="row" spacing={1} alignItems="baseline" pl={2}>
													<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'envelope' }} fixedWidth style={{ transform: 'translateY(3px)'}} />
													<Link href="mailto:timkuligfreemusic@gmail.com" sx={{ overflowWrap: 'anywhere'}}>timkuligfreemusic@gmail.com</Link>
												</Stack>
												<Stack direction="row" spacing={1} alignItems="center" pl={2}>
													<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'phone' }} fixedWidth />
													<Link href="tel:+1-941-567-7121">+1 (941) 567-7121</Link>
												</Stack>
											</Stack>
										</BodyText>
									</Stack>
								</Paper>
							</Grid>
						</Grid>
					</Stack>
					<Stack component="section" spacing={4} px={4} maxWidth={700}>
						<HeadingText2>Let's stay in touch!</HeadingText2>
						<BodyText>
							It'd be great to get your contact info so that I can update you about where you
							can download my music, updates to the site, special releases and deals, podcasts,
							all sorts of stuff!
						</BodyText>
						{!submitSuccessful && (
							<form ref={contactForm} onSubmit={handleSubmitContactForm} noValidate>
								<Stack spacing={4}>
									<Stack direction="row" spacing={2}>
										<TextField
											name="firstName"
											label="First Name"
											fullWidth
											required
											variant="standard"
											value={contactInfo.firstName}
											onChange={handleChange}
										/>
										<TextField
											name="lastName"
											label="Last Name"
											fullWidth
											variant="standard"
											value={contactInfo.lastName}
											onChange={handleChange}
										/>
									</Stack>
									<TextField
										name="email"
										label="Email"
										fullWidth
										required
										variant="standard"
										value={contactInfo.email}
										onChange={handleChange}
										onBlur={validateEmail}
										error={!!emailError}
										helperText={emailError}
									/>
									<FormControlLabel
										control={<Checkbox name="signUp" checked={contactInfo.signUp} onChange={handleSwitchChange} />}
										label="Sign up for news & updates"
										slotProps={{ typography: { sx: { color: 'rgba(255, 255, 255, 0.7)' } } }}
									/>
									<TextField
										name="phone"
										label="Phone (if it's easier for us to talk!)"
										fullWidth
										variant="standard"
										value={contactInfo.phone}
										onChange={handleChange}
									/>
									<Stack spacing={1}>
										<Typography variant="body1" color="rgba(255, 255, 255, 0.7)">What brings you to Tim Kulig Free Music?</Typography>
										<FormGroup sx={{ pl: 1 }}>
											<FormControlLabel
												control={<Checkbox name="freeMusic" checked={contactInfo.freeMusic} onChange={handleSwitchChange} />}
												label="The free music, duh!"
												slotProps={{ typography: { sx: { color: 'rgba(255, 255, 255, 0.7)' } } }}
											/>
											<FormControlLabel
												control={<Checkbox name="likeYourStuff" checked={contactInfo.likeYourStuff} onChange={handleSwitchChange} />}
												label="I like your stuff and want to stay informed!"
												slotProps={{ typography: { sx: { color: 'rgba(255, 255, 255, 0.7)' } } }}
											/>
											<FormControlLabel
												control={<Checkbox name="networking" checked={contactInfo.networking} onChange={handleSwitchChange} />}
												label="Expanding my friends group / networking / staying connected"
												slotProps={{ typography: { sx: { color: 'rgba(255, 255, 255, 0.7)' } } }}
											/>
											<FormControlLabel
												control={<Checkbox name="fan" checked={contactInfo.fan} onChange={handleSwitchChange} />}
												label="Does it matter? I'm a fan! :D"
												slotProps={{ typography: { sx: { color: 'rgba(255, 255, 255, 0.7)' } } }}
											/>
										</FormGroup>
									</Stack>
									<TextField
										name="musicStyles"
										label="If you're a content creator, what styles / kinds of music are you looking for from me?"
										fullWidth
										variant="standard"
										value={contactInfo.musicStyles}
										onChange={handleChange}
									/>
									<FormControl sx={{ width: '100%' }} variant="standard">
										<InputLabel id="ref-src-lbl">How did you hear about me or my music?</InputLabel>
										<Select
											labelId="ref-src-lbl"
											id="ref-src"
											name="referralSource"
											label="How did you hear about me or my music?"
											value={contactInfo.referralSource}
											onChange={handleSelectChange}
										>
											<MenuItem value="Google">Google search</MenuItem>
											<MenuItem value="FilmMusic.io">FilmMusic.io</MenuItem>
											<MenuItem value="Pixabay">Pixabay</MenuItem>
											<MenuItem value="SoundCloud">SoundCloud</MenuItem>
											<MenuItem value="YouTube">YouTube</MenuItem>
											<MenuItem value="Other">Other</MenuItem>
										</Select>
									</FormControl>
									<TextField
										name="message"
										label="Message"
										fullWidth
										multiline
										required
										minRows={3}
										variant="standard"
										value={contactInfo.message}
										onChange={handleChange}
									/>
									<Button variant="contained" size="large" type="submit">Send</Button>
									<Typography variant="caption" color="textSecondary">* Indicates a required field.</Typography>
								</Stack>
							</form>
						)}
						{submitSuccessful && (
							<Alert severity="success" variant="outlined">
								<AlertTitle>Success!</AlertTitle>
								Your message has been sent. Thank you for reaching out!
							</Alert>
						)}
					</Stack>
				</Stack>
			)}
		/>
	);
};

export default ContactScreen;
